import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {apiRequest} from "../../utils/AxiosHelper";
import {jwtDecode} from 'jwt-decode';
import {getCookie, deleteCookie, Cookies} from '../../utils/cookies';
import {
  SessionState,
  JwtPayloadLocal,
  LoginCredentials,
  AuthResponse,
  SessionValidationResponse,
  SessionErrorPayload
} from '../../types';

const SESSION_DURATION = 15 * 60 * 1000; // 15 minutes
const WARNING_TIME = 5 * 60 * 1000; // 5 minutes before expiry

const initialState: SessionState = {
  user: null,
  token: null,
  isAuthenticated: false,
  sessionExpiry: null,
  lastActivity: Date.now(),
  loading: false
}

// Async Thunks
export const authenticateUser = createAsyncThunk<AuthResponse, LoginCredentials>(
  'session/authenticateUser',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        type: 'LOGIN_FAILED',
        message: error.response?.data?.message || 'Login failed'
      });
    }
  }
);

export const validateSession = createAsyncThunk<SessionValidationResponse, void, { rejectValue: SessionErrorPayload }>(
  'session/validateSession',
  async (_, { rejectWithValue }) => {
    try {
      const userAuthCookie = getCookie(Cookies.AUTH_SUCCESS);

      if (!userAuthCookie || userAuthCookie !== 'true') {
        return rejectWithValue({
          type: 'NO_COOKIE',
          message: 'No valid authentication cookie found'
        });
      }

      const response = await apiRequest.get('/auth/token', {
        withCredentials: true,
        timeout: 5000
      });

      return response.data;
    } catch (error: any) {
      deleteCookie(Cookies.AUTH_SUCCESS);
      return rejectWithValue({
        type: 'SESSION_INVALID',
        message: error.response?.data?.message || 'Session validation failed'
      });
    }
  }
);

export const refreshSessionToken = createAsyncThunk<SessionValidationResponse, void, { rejectValue: SessionErrorPayload }>(
  'session/refreshSessionToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post('/auth/refresh', {}, {
        withCredentials: true,
        timeout: 5000
      });

      return response.data;
    } catch (error: any) {
      deleteCookie(Cookies.AUTH_SUCCESS);
      return rejectWithValue({
        type: 'REFRESH_FAILED',
        message: error.response?.data?.message || 'Session refresh failed'
      });
    }
  }
);

export const logoutUser = createAsyncThunk<void, void, { rejectValue: SessionErrorPayload }>(
  'session/logoutUser',
  async (_, { rejectWithValue }) => {
    try {
      await apiRequest.post('/auth/logout', {}, {
        withCredentials: true,
        timeout: 3000
      });
      deleteCookie(Cookies.AUTH_SUCCESS);
    } catch (error: any) {
      deleteCookie(Cookies.AUTH_SUCCESS);
      console.warn('Logout API failed, but session cleared locally:', error);
    }
  }
);

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    updateLastActivity: (state) => {
      state.lastActivity = Date.now();
    },

    clearSession: (state) => {
      resetSessionState(state);
    },

    setLoadingState: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    updateSessionFromToken: (state, action: PayloadAction<{ token: string; preserveUserData?: boolean }>) => {
      const { token, preserveUserData = false } = action.payload;
      handleTokenUpdate(state, token, preserveUserData);
    },

    extendSessionManually: (state, action: PayloadAction<number>) => {
      if (state.isAuthenticated && state.sessionExpiry) {
        state.sessionExpiry = Date.now() + action.payload;
        state.lastActivity = Date.now();
      }
    }
  },

  extraReducers: (builder) => {
    builder
      // Authentication cases
      .addCase(authenticateUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(authenticateUser.fulfilled, (state, action) => {
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        state.sessionExpiry = Date.now() + SESSION_DURATION;
        state.lastActivity = Date.now();
        state.loading = false;
      })
      .addCase(authenticateUser.rejected, (state, action) => {
        resetSessionState(state);
        state.loading = false;
      })

      // Session validation cases
      .addCase(validateSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        if (action.payload?.token) {
          handleTokenUpdate(state, action.payload.token, false);
        } else {
          resetSessionState(state);
        }
        state.loading = false;
      })
      .addCase(validateSession.rejected, (state, action) => {
        resetSessionState(state);
        state.loading = false;
        console.error('Session validation failed:', action.payload?.message);
      })

      // Session refresh cases
      .addCase(refreshSessionToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshSessionToken.fulfilled, (state, action) => {
        if (action.payload?.token) {
          handleTokenUpdate(state, action.payload.token, true);
        } else {
          resetSessionState(state);
        }
        state.loading = false;
      })
      .addCase(refreshSessionToken.rejected, (state, action) => {
        resetSessionState(state);
        state.loading = false;
        console.error('Session refresh failed:', action.payload?.message);
      })

      // Logout cases
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        resetSessionState(state);
        state.loading = false;
      })
      .addCase(logoutUser.rejected, (state) => {
        resetSessionState(state);
        state.loading = false;
      });
  }
});

// Helper functions
function resetSessionState(state: SessionState): void {
  state.user = null;
  state.token = null;
  state.isAuthenticated = false;
  state.sessionExpiry = null;
  state.lastActivity = Date.now();
}

function handleTokenUpdate(state: SessionState, token: string, preserveUserData: boolean): void {
  try {
    const tokenPayload = jwtDecode<JwtPayloadLocal>(token);

    state.user = {
      id: tokenPayload.sub,
      name: tokenPayload.name || (preserveUserData ? state.user?.name : undefined) || 'Unknown User',
      email: tokenPayload.email || (preserveUserData ? state.user?.email : undefined) || '',
      role: (tokenPayload.role as 'admin' | 'analyst' | 'viewer') || (preserveUserData ? state.user?.role : undefined) || 'viewer',
      permissions: tokenPayload.permissions || (preserveUserData ? state.user?.permissions : undefined) || []
    };

    state.token = token;
    state.isAuthenticated = true;
    state.sessionExpiry = tokenPayload.exp * 1000;
    state.lastActivity = Date.now();
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    resetSessionState(state);
  }
}


export const selectIsAuthenticated = (state: { session: SessionState }) => state.session.isAuthenticated;
export const selectUser = (state: { session: SessionState }) => state.session.user;
export const selectSessionExpiry = (state: { session: SessionState }) => state.session.sessionExpiry;
export const selectIsSessionExpiringSoon = (state: { session: SessionState }) => {
  const { sessionExpiry } = state.session;
  if (!sessionExpiry) return false;
  return (sessionExpiry - Date.now()) <= WARNING_TIME;
};
export const selectSessionTimeRemaining = (state: { session: SessionState }) => {
  const { sessionExpiry } = state.session;
  if (!sessionExpiry) return 0;
  return Math.max(0, sessionExpiry - Date.now());
};

export const {updateLastActivity} = sessionSlice.actions;
export default sessionSlice.reducer;
